import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MAILTO_SUPPORT_HREF } from '../../constants/publicSite';
import Login from '../auth/Login';
import LandingNav from './LandingNav';
import './Home.css';

const PillarIcon1 = () => (
  <svg
    className="landing-pillar-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 7H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3" />
    <rect x="9" y="3" width="6" height="6" rx="1" />
    <path d="M9 14h6M9 17h4" />
  </svg>
);

const PillarIcon2 = () => (
  <svg
    className="landing-pillar-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);

const PillarIcon3 = () => (
  <svg
    className="landing-pillar-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1" fill="currentColor" />
  </svg>
);

export default function Home({
  onLogin,
  showLoginInline = false,
  onLoginSuccess,
}: {
  onLogin: () => void;
  onBack?: () => void;
  showLoginInline?: boolean;
  onLoginSuccess?: () => void;
}) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const landingRef = useRef(null);

  const handleLoginClick = () => {
    if (showLoginInline) setShowLoginModal(true);
    else onLogin();
  };

  useEffect(() => {
    const el = landingRef.current as HTMLDivElement | null;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const parent = e.target.parentElement;
          if (!parent) return;
          const siblings = [...parent.querySelectorAll('.landing-reveal:not(.in)')];
          const i = siblings.indexOf(e.target);
          setTimeout(() => e.target.classList.add('in'), i * 100);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll('.landing-reveal').forEach((node) => io.observe(node));
    return () => io.disconnect();
  }, []);

  return (
    <div className="landing-page" ref={landingRef}>
      {showLoginModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowLoginModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
            <Login
              embedded
              onBack={() => setShowLoginModal(false)}
              onSuccess={() => {
                setShowLoginModal(false);
                onLoginSuccess?.();
              }}
            />
          </div>
        </div>
      )}

      <LandingNav mode="marketing" onSignInClick={handleLoginClick} />

      <section className="landing-hero">
        <div className="landing-hero-inner">
          <span className="landing-eyebrow landing-fade0">Automated Section 280G Analysis</span>
          <h1 className="landing-fade1">
            The all-in-one
            <br />
            <em>280G platform.</em>
          </h1>
          <p className="landing-hero-sub landing-fade2">
            From analysis to vote materials to shareholder approval — every step of a Section 280G engagement, in one
            place.
          </p>
          <div className="landing-hero-actions landing-fade3">
            <button type="button" onClick={handleLoginClick} className="landing-btn-primary">
              Request Early Access
            </button>
            <a href="#pillars" className="landing-btn-text">
              See how it works <span>→</span>
            </a>
          </div>
        </div>
      </section>

      <section className="landing-pillars" id="pillars">
        <div className="landing-wrap">
          <span className="landing-eyebrow landing-reveal">What ParachuteIQ does</span>
          <p className="landing-pillars-intro landing-reveal">Three steps. One platform. No spreadsheets.</p>

          <div className="landing-pillars-grid">
            <div className="landing-pillar landing-reveal">
              <PillarIcon1 />
              <span className="landing-pillar-num">01</span>
              <div className="landing-pillar-title">Run your analysis.</div>
              <p className="landing-pillar-body">
                Enter your deal data and let ParachuteIQ handle the computation. Built by professionals who know Section
                280G — so you don&apos;t have to rebuild the model on every transaction.
              </p>
            </div>
            <div className="landing-pillar landing-reveal">
              <PillarIcon2 />
              <span className="landing-pillar-num">02</span>
              <div className="landing-pillar-title">Generate your vote materials.</div>
              <p className="landing-pillar-body">
                Produce disclosure-ready documentation directly from your analysis. No manual assembly, no copying
                numbers between tabs. Attorney-ready output in one step.
              </p>
            </div>
            <div className="landing-pillar landing-reveal">
              <PillarIcon3 />
              <span className="landing-pillar-num">03</span>
              <div className="landing-pillar-title">Securely conduct the shareholder vote.</div>
              <p className="landing-pillar-body">
                Manage the §280G shareholder approval process end-to-end within the platform. Secure, documented, and
                compliant — without the coordination headache.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-built" id="built">
        <div className="landing-wrap">
          <div className="landing-built-grid">
            <div className="landing-reveal">
              <span className="landing-eyebrow">Built by practitioners</span>
              <h2 className="landing-built-headline">We&apos;ve spent decades doing this work.</h2>
              <p className="landing-built-body">
                ParachuteIQ was built by compensation professionals who have spent their careers working Section 280G
                engagements — not engineers guessing at the tax code. We know the rules, the edge cases, and the pain of
                doing this in spreadsheets. We built the tool we always wished existed.
              </p>
            </div>
            <div className="landing-built-stats landing-reveal">
              <div className="landing-b-stat">
                <div className="landing-b-stat-num">20+</div>
                <div className="landing-b-stat-label">Years of 280G practitioner experience behind the platform</div>
              </div>
              <div className="landing-b-stat">
                <div className="landing-b-stat-num">500+</div>
                <div className="landing-b-stat-label">M&A transactions analyzed by the founding team</div>
              </div>
              <div className="landing-b-stat">
                <div className="landing-b-stat-num">90%</div>
                <div className="landing-b-stat-label">Reduction in time from data input to final deliverable</div>
              </div>
              <div className="landing-b-stat">
                <div className="landing-b-stat-num">1</div>
                <div className="landing-b-stat-label">Platform for the entire 280G engagement, start to finish</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-cta-section">
        <div className="landing-wrap landing-reveal">
          <span className="landing-eyebrow">Get started</span>
          <h2 className="landing-cta-headline">Ready to simplify your next 280G engagement?</h2>
          <p className="landing-cta-sub">
            Request early access and see how ParachuteIQ handles your engagement from start to finish.
          </p>
          <div className="landing-cta-actions">
            <button type="button" onClick={handleLoginClick} className="landing-btn-white">
              Request a Demo
            </button>
            <a href="#pillars" className="landing-btn-ghost">
              Learn more <span>→</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <Link to="/" className="landing-footer-logo">
          parachute<span className="iq">IQ</span>
        </Link>
        <ul className="landing-footer-links">
          <li>
            <a href="#pillars">Platform</a>
          </li>
          <li>
            <a href="#built">Pricing</a>
          </li>
          <li>
            <Link to="/privacy">Privacy</Link>
          </li>
          <li>
            <Link to="/terms">Terms</Link>
          </li>
          <li>
            <a href={MAILTO_SUPPORT_HREF}>Contact</a>
          </li>
        </ul>
        <span className="landing-footer-copy">© 2026 ParachuteIQ</span>
      </footer>
    </div>
  );
}
